/**
 * LINE 智慧客服 — REQ-20260518001。
 *
 * - DB key='line.bot',accessToken / channelSecret 以 AES-256-GCM 加密
 * - View 只回 hasAccessToken / hasChannelSecret(不回原值)
 * - Input 中 accessToken / channelSecret 留空 = 保留原值(同 telegram / smtp 模式)
 */
import { z } from 'zod';

export const LINE_DEFAULT_AI_PROMPT = `你是一位友善的客服助理,代表本公司回覆客戶在 LINE 上的訊息。
- 用繁體中文,口語、簡短、有禮貌
- 不確定答案時誠實說「我幫您轉客服人員」並停止回覆
- 不主動承諾交期、價格、退款等具體條件
- 一次回覆控制在 3 句以內`;

/** REQ-20260519002:AI 助理可參考的服務項目資料(每筆一行短說明) */
export const LineServiceItemSchema = z.object({
  name: z.string().trim().min(1, '名稱不可為空').max(100),
  description: z.string().trim().max(500).default(''),
});
export type LineServiceItem = z.infer<typeof LineServiceItemSchema>;

export const LineSettingInputSchema = z.object({
  enabled: z.boolean().default(false),
  accessToken: z.string().max(500).default(''),
  channelSecret: z.string().max(200).default(''),
  aiEnabled: z.boolean().default(true),
  aiSystemPrompt: z.string().max(8000).default(''),
  /**
   * REQ-20260519002:服務項目清單(會被注入到 AI 回覆的 system message)。
   * `undefined` = 此次請求不更動服務項目(由舊版表單送出時不會帶到欄位)。
   */
  serviceItems: z.array(LineServiceItemSchema).max(100).optional(),
});
export type LineSettingInput = z.infer<typeof LineSettingInputSchema>;

export const LineSettingViewSchema = z.object({
  enabled: z.boolean(),
  hasAccessToken: z.boolean(),
  hasChannelSecret: z.boolean(),
  aiEnabled: z.boolean(),
  aiSystemPrompt: z.string(),
  serviceItems: z.array(LineServiceItemSchema),
  updatedAt: z.string().datetime().nullable(),
});
export type LineSettingView = z.infer<typeof LineSettingViewSchema>;

export const LineSettingTestResultSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
});
export type LineSettingTestResult = z.infer<typeof LineSettingTestResultSchema>;

// ── 對話 / 訊息 ──────────────────────────────────────────────

export const LINE_CONTACT_STATUSES = ['AI', 'HUMAN', 'CLOSED'] as const;
export const LineContactStatusSchema = z.enum(LINE_CONTACT_STATUSES);
export type LineContactStatus = z.infer<typeof LineContactStatusSchema>;

export const LINE_MESSAGE_DIRECTIONS = ['IN', 'OUT'] as const;
export const LineMessageDirectionSchema = z.enum(LINE_MESSAGE_DIRECTIONS);
export type LineMessageDirection = z.infer<typeof LineMessageDirectionSchema>;

export const LINE_MESSAGE_TYPES = [
  'TEXT',
  'IMAGE',
  'VIDEO',
  'AUDIO',
  'FILE',
  'STICKER',
  'SYSTEM',
] as const;
export const LineMessageTypeSchema = z.enum(LINE_MESSAGE_TYPES);
export type LineMessageType = z.infer<typeof LineMessageTypeSchema>;

export const LINE_SENDER_TYPES = ['LINE_USER', 'AI', 'AGENT', 'SYSTEM'] as const;
export const LineSenderTypeSchema = z.enum(LINE_SENDER_TYPES);
export type LineSenderType = z.infer<typeof LineSenderTypeSchema>;

export const LineContactListItemSchema = z.object({
  id: z.number().int(),
  lineUserId: z.string(),
  displayName: z.string().nullable(),
  pictureUrl: z.string().nullable(),
  status: LineContactStatusSchema,
  assignedToId: z.number().int().nullable(),
  assignedToName: z.string().nullable(),
  /** REQ-20260518002:綁定的客戶 id;null = 未綁定 */
  customerId: z.number().int().nullable(),
  customerSimpleName: z.string().nullable(),
  customerCCode: z.string().nullable(),
  lastMessageAt: z.string().datetime().nullable(),
  lastMessagePreview: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
});
export type LineContactListItem = z.infer<typeof LineContactListItemSchema>;

export const LineContactListResponseSchema = z.object({
  items: z.array(LineContactListItemSchema),
  nextCursor: z.string().nullable(),
});
export type LineContactListResponse = z.infer<typeof LineContactListResponseSchema>;

export const LineMessageAttachmentViewSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
});
export type LineMessageAttachmentView = z.infer<typeof LineMessageAttachmentViewSchema>;

export const LineMessageViewSchema = z.object({
  id: z.number().int(),
  contactId: z.number().int(),
  direction: LineMessageDirectionSchema,
  messageType: LineMessageTypeSchema,
  text: z.string().nullable(),
  stickerPackageId: z.string().nullable(),
  stickerStickerId: z.string().nullable(),
  attachment: LineMessageAttachmentViewSchema.nullable(),
  senderType: LineSenderTypeSchema,
  agentUserId: z.number().int().nullable(),
  agentUserName: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type LineMessageView = z.infer<typeof LineMessageViewSchema>;

export const LineContactDetailSchema = LineContactListItemSchema.extend({
  messages: z.array(LineMessageViewSchema),
});
export type LineContactDetail = z.infer<typeof LineContactDetailSchema>;

export const LineAgentSendInputSchema = z.object({
  text: z.string().trim().min(1, '訊息不可為空').max(5000),
});
export type LineAgentSendInput = z.infer<typeof LineAgentSendInputSchema>;

// ── REQ-20260518002:綁定客戶 + 附加客戶附件 ─────────────────────────

export const LineBindCustomerInputSchema = z.object({
  customerId: z.number().int().positive(),
});
export type LineBindCustomerInput = z.infer<typeof LineBindCustomerInputSchema>;

/** 客戶可選附件(用於後台 modal 選一份附件送給 LINE 客戶) */
export const LineCustomerAttachmentItemSchema = z.object({
  uuid: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  interviewId: z.number().int(),
  interviewCreatedAt: z.string().datetime(),
  interviewTypeName: z.string().nullable(),
  /** 是否能用 LINE 原生 image 訊息(jpg/png 且 ≤ 1 MB);否則走文字 + 連結 */
  canSendAsImage: z.boolean(),
});
export type LineCustomerAttachmentItem = z.infer<typeof LineCustomerAttachmentItemSchema>;

export const LineCustomerAttachmentListSchema = z.object({
  items: z.array(LineCustomerAttachmentItemSchema),
});
export type LineCustomerAttachmentList = z.infer<typeof LineCustomerAttachmentListSchema>;

/** REQ-20260519003:以「訪談」為單位的清單,每筆內含該訪談的活附件 */
export const LineCustomerInterviewSchema = z.object({
  interviewId: z.number().int(),
  interviewCreatedAt: z.string().datetime(),
  interviewTypeName: z.string().nullable(),
  contact: z.string().nullable(),
  notesPreview: z.string().nullable(),
  attachments: z.array(LineCustomerAttachmentItemSchema),
});
export type LineCustomerInterview = z.infer<typeof LineCustomerInterviewSchema>;

export const LineCustomerInterviewListSchema = z.object({
  customerId: z.number().int(),
  customerSimpleName: z.string(),
  customerCCode: z.string().nullable(),
  items: z.array(LineCustomerInterviewSchema),
});
export type LineCustomerInterviewList = z.infer<typeof LineCustomerInterviewListSchema>;

export const LineAgentSendAttachmentInputSchema = z.object({
  attachmentUuid: z.string().uuid(),
});
export type LineAgentSendAttachmentInput = z.infer<typeof LineAgentSendAttachmentInputSchema>;

// ── REQ-20260519001:用 OpenAI 生 LINE Rich Menu 圖片 ───────────────

export const LINE_RICHMENU_SIZES = ['1536x1024', '1024x1024', '1024x1536'] as const;
export const LineRichMenuSizeSchema = z.enum(LINE_RICHMENU_SIZES);
export type LineRichMenuSize = z.infer<typeof LineRichMenuSizeSchema>;

export const LINE_RICHMENU_QUALITIES = ['low', 'medium', 'high'] as const;
export const LineRichMenuQualitySchema = z.enum(LINE_RICHMENU_QUALITIES);
export type LineRichMenuQuality = z.infer<typeof LineRichMenuQualitySchema>;

export const LineRichMenuGenerateInputSchema = z.object({
  prompt: z.string().trim().min(5, '提示詞請至少 5 字').max(4000),
  size: LineRichMenuSizeSchema.default('1536x1024'),
  quality: LineRichMenuQualitySchema.default('medium'),
});
export type LineRichMenuGenerateInput = z.infer<typeof LineRichMenuGenerateInputSchema>;

export const LineRichMenuGenerateResultSchema = z.object({
  /** base64 PNG。前端可組成 data URL 預覽 / 下載。 */
  b64: z.string(),
  mimeType: z.string(),
  size: z.string(),
  model: z.string(),
  latencyMs: z.number(),
});
export type LineRichMenuGenerateResult = z.infer<typeof LineRichMenuGenerateResultSchema>;

/** 預設「詢價」按鈕 Rich Menu 提示詞 */
export const LINE_RICHMENU_DEFAULT_PROMPT = `Design a clean, professional LINE rich menu banner image with a single big call-to-action button.
- Width-to-height roughly 3:2(matching LINE rich menu full layout)
- Single button in the center,large bold Traditional Chinese text "詢價"
- Modern flat design,bright blue gradient button background,white text
- Subtle business-themed icons around (like price tag, calculator, message bubble),pale grey tones
- Plain off-white background,no other text,no logo,no people
- Clear margins so the button area is the dominant visual focal point`;
